import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'
import { coursePeriod } from '../courses/period.js'
import { ApplyModal } from '../components/ApplyModal.jsx'

// Public course detail: description, schedule, capacity, registration window,
// and the "Apply a participant" button → its own page (no modals).

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export function CourseDetailPage() {
  const { t } = useTranslation()
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()
  const [course, setCourse] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showApply, setShowApply] = useState(false)
  const [applyMode, setApplyMode] = useState('enroll') // 'enroll' | 'waitlist'
  const [error, setError] = useState(null)

  function openApply(mode = 'enroll') {
    if (!authLoading && !user) {
      navigate('/login')
      return
    }
    setApplyMode(mode)
    setShowApply(true)
  }

  useEffect(() => {
    setLoading(true); setError(null)
    api.courses.get(slug)
      .then((d) => setCourse(d.course))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (error || !course) {
    return (
      <main className="container">
        <p className="error">{t('courses.errors.not_found')}</p>
        <p><Link to="/courses">{t('courses.back_to_list')}</Link></p>
      </main>
    )
  }

  const c = course
  return (
    <main className="container">
      <p><Link to="/courses">← {t('courses.back_to_list')}</Link></p>
      <h1>{c.featured === 1 && '★ '}{c.name}</h1>
      <p className="muted">{c.category_name}{c.teacher_name ? ` · ${t('courses.teacher')}: ${c.teacher_name}` : ''}</p>

      {c.image_url && (
        <img src={c.image_url} alt="" style={{ maxWidth: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: '8px' }} />
      )}

      {c.description && <p>{c.description}</p>}

      <p>
        {c.price !== null
          ? <><strong>{fmtPrice(c.price)}</strong></>
          : <span className="muted">{t('courses.contact_teacher_price')}</span>}
      </p>
      {c.teacher_phone && (
        <p>{t('courses.teacher_phone', { phone: c.teacher_phone })}</p>
      )}

      {coursePeriod(t, c.date_start, c.date_end) && (
        <p className="muted">{coursePeriod(t, c.date_start, c.date_end)}</p>
      )}
      {c.registration_end && (
        <p className="muted">{t('courses.registration_until', { date: c.registration_end })}</p>
      )}
      {c.is_open && c.days_to_register !== null && c.days_to_register <= 5 && (
        <p className="error">{t('courses.days_left', { count: c.days_to_register })}</p>
      )}
      {c.capacity_left !== null && (
        <p className="muted">{t('courses.seats_left', { count: c.capacity_left })}</p>
      )}

      {(c.schedules || []).length > 0 && (
        <>
          <h2>{t('courses.schedule_title')}</h2>
          <ul>
            {c.schedules.map((s) => (
              <li key={s.id}>
                {t(`courses.day_${DAY_KEYS[s.day_of_week]}`)} {String(s.time).slice(0, 5)}
                {s.notes ? ` · ${s.notes}` : ''}
              </li>
            ))}
          </ul>
          <p className="muted small">
            {t(`courses.repeat_${c.repeat_type}`, { count: c.repeat_period })}
          </p>
        </>
      )}

      {c.managed !== 'self' && (
        c.is_open ? (
          <p><button type="button" onClick={() => openApply('enroll')}>{t('courses.apply')}</button></p>
        ) : c.waitlist_open ? (
          // Every seat is taken but the waiting list is on: queue up instead.
          <>
            <p className="muted">{t('courses.full_waitlist')}</p>
            <p>
              <button type="button" onClick={() => openApply('waitlist')}>
                {t('courses.waitlist_join')}
              </button>
            </p>
          </>
        ) : (
          <p className="muted">{t('courses.registration_closed')}</p>
        )
      )}

      {showApply && (
        <ApplyModal course={c} mode={applyMode} onClose={() => setShowApply(false)} />
      )}
    </main>
  )
}
